import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  List, 
  Tag, 
  Button, 
  Space,
  Spin
} from 'antd';
import { 
  FileAddOutlined, 
  SearchOutlined, 
  TeamOutlined, 
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { queryShipments } from '../services/shipmentService';
import { getCustomers } from '../services/customerService';
import { formatDate, getTimeDiff } from '../utils/dateUtils';

const { Title, Text } = Typography;

const Home = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalShipments: 0,
    inStock: 0,
    outStock: 0,
    customerCount: 0
  });
  const [recentShipments, setRecentShipments] = useState([]);
  
  // 加载统计数据和最近运单
  const loadData = async () => {
    setLoading(true);
    try {
      console.log('开始加载首页数据');
      
      // 使用Promise.allSettled来并行请求，即使某些请求失败也不会影响其他请求
      const [
        shipmentsResult,
        totalResult,
        inStockResult,
        outStockResult,
        customersResult
      ] = await Promise.allSettled([
        // 获取运单统计
        queryShipments({ pageSize: 5 }).catch(err => {
          console.error('获取最近运单失败:', err);
          return { items: [] };
        }),
        
        // 获取总运单数
        queryShipments({ countOnly: true }).catch(err => {
          console.error('获取总运单数失败:', err);
          return { total: 0 };
        }),
        
        // 获取入库运单数
        queryShipments({ status: '已入库', countOnly: true }).catch(err => {
          console.error('获取入库运单数失败:', err);
          return { total: 0 };
        }),
        
        // 获取出库运单数
        queryShipments({ status: '已出库', countOnly: true }).catch(err => {
          console.error('获取出库运单数失败:', err);
          return { total: 0 };
        }),
        
        // 获取客户数量
        getCustomers({ countOnly: true }).catch(err => {
          console.error('获取客户数量失败:', err);
          return { total: 0 };
        })
      ]);
      
      // 处理最近运单结果
      if (shipmentsResult.status === 'fulfilled') {
        const shipmentsData = shipmentsResult.value;
        console.log('获取到最近运单:', shipmentsData);
        setRecentShipments(shipmentsData.items || []);
      } else {
        console.error('获取最近运单失败:', shipmentsResult.reason);
        setRecentShipments([]);
      }
      
      // 处理统计数据
      const totalShipments = totalResult.status === 'fulfilled' ? totalResult.value.total || 0 : 0;
      const inStock = inStockResult.status === 'fulfilled' ? inStockResult.value.total || 0 : 0;
      const outStock = outStockResult.status === 'fulfilled' ? outStockResult.value.total || 0 : 0;
      const customerCount = customersResult.status === 'fulfilled' ? 
        (Array.isArray(customersResult.value) ? customersResult.value.length : 
         (customersResult.value && customersResult.value.total) || 0) : 0;
      
      console.log('统计数据:', { totalShipments, inStock, outStock, customerCount });
      
      setStats({
        totalShipments,
        inStock,
        outStock,
        customerCount
      });
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // 组件加载时获取数据
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  return (
    <div className="home-page">
      <Spin spinning={loading}>
        <Title level={4}>欢迎, {currentUser ? currentUser.username : '用户'}</Title>
        
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="总运单数"
                value={stats.totalShipments}
                prefix={<FileAddOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="入库中"
                value={stats.inStock}
                prefix={<ArrowDownOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="已出库"
                value={stats.outStock}
                prefix={<ArrowUpOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="客户数量"
                value={stats.customerCount}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Card 
              title="最近运单" 
              extra={<Link to="/query"><Button type="link">查看全部</Button></Link>}
            >
              <List
                dataSource={recentShipments}
                renderItem={item => (
                  <List.Item
                    actions={[
                      <Link to={`/edit/${item.id}`} key="view">查看详情</Link>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{item.tracking}</Text>
                          <Tag color={
                            item.status === '已入库' || item.status === 'instock' ? 'blue' : 
                            item.status === '已出库' || item.status === 'shipped' ? 'green' : 
                            item.status === '运输中' ? 'orange' : 
                            item.status === '已签收' || item.status === 'delivered' ? 'green' : 
                            item.status === 'completed' ? 'green' : 'default'
                          }>
                            {item.status === 'instock' ? '已入库' : 
                             item.status === 'shipped' ? '已出库' : 
                             item.status === 'delivered' ? '已签收' : 
                             item.status === 'completed' ? '已完成' : 
                             item.status}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text>{item.name} | {item.phone}</Text>
                          <Text type="secondary">{item.content}</Text>
                        </Space>
                      }
                    />
                    <div>
                      <Text type="secondary">{formatDate(item.date)}</Text>
                      <br />
                      <Text type="secondary">{getTimeDiff(item.created_at)}</Text>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          
          <Col xs={24} md={8}>
            <Card title="快捷操作">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  icon={<FileAddOutlined />} 
                  size="large" 
                  block
                  onClick={() => window.location.href = '/submit'}
                >
                  提交运单
                </Button>
                <Button 
                  icon={<SearchOutlined />} 
                  size="large" 
                  block
                  onClick={() => window.location.href = '/query'}
                >
                  查询运单
                </Button>
                <Button 
                  icon={<TeamOutlined />} 
                  size="large" 
                  block
                  onClick={() => window.location.href = '/customers'}
                >
                  客户管理
                </Button>
              </Space>
            </Card>
            
            <Card title="系统信息" style={{ marginTop: 16 }}>
              <p><strong>当前版本:</strong> 1.0.0</p>
              <p><strong>最后更新:</strong> {formatDate(Date.now())}</p>
              <p>
                <ClockCircleOutlined /> 系统时间: {new Date().toLocaleString('zh-CN', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false
                })}
              </p>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Home;
